import { useMemo, useReducer, memo } from 'react'

import { Box, IconButton, InputAdornment, TextField } from '@mui/material'
import { IconArrowBackUp, IconCircleArrowUpFilled } from '@tabler/icons-react'
import type { ActionPromptOutput, CRMObject, Page } from 'types/graphql'

import Row from 'src/components/Row/Row'
import ObjectContextManager from 'src/components/Threads/ObjectContextManager/ObjectContextManager'
import { shadows } from 'src/lib/style'

import type { ContextStatus } from '../Threads/V2/Provider/ThreadContext'

import type { PromptState } from './utils'

const containerWrapperSx = {
  mb: 2,
  mx: 1,
  mt: 1,
  borderRadius: '12px',
  boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
}

const upperContainerSx = {
  background: (theme) => theme.palette.background.paper,
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  p: 1,
  position: 'relative',
}

const lowerContainerSx = {
  background: (theme) => theme.palette.background.paper,
  borderBottomLeftRadius: '12px',
  borderBottomRightRadius: '12px',
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderTop: 'none',
  p: 1,
  position: 'relative',
}

const navigationButtonsSx = {
  position: 'absolute',
  top: 8,
  right: 8,
  display: 'flex',
  gap: 0.5,
  zIndex: 10,
}

const promptContainerSx = {
  position: 'relative',
  width: '100%',
  height: 0,
  zIndex: 20,
}

const actionButtonContainerSx = {
  position: 'absolute',
  right: 8,
  bottom: 8,
}

const rootBoxSx = {
  position: 'relative',
}

const getTextFieldInputSx = () => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'end',
  p: '4px',
  pt: '12px',
  pl: '12px',
  letterSpacing: '-0.55px',
  fontWeight: 400,
  color: 'text.primary',
})

type PromptAction =
  | { type: 'SET_TEMPLATE'; payload: Page | null }
  | { type: 'UPDATE_TEMPLATE'; payload: Page | null }
  | { type: 'EDIT_PROMPT'; payload: string }
  | { type: 'UNDO' }

const promptReducer = (
  state: PromptState,
  action: PromptAction
): PromptState => {
  switch (action.type) {
    case 'SET_TEMPLATE':
      return {
        ...state,
        template:
          state.template?.id === action.payload?.id ? null : action.payload,
      }
    case 'UPDATE_TEMPLATE':
      return {
        ...state,
        template: action.payload,
      }
    case 'EDIT_PROMPT':
      return {
        ...state,
        isEdited: true,
        prompt: action.payload,
      }
    case 'UNDO': {
      return {
        ...state,
        template: state.originalTemplate ? { ...state.originalTemplate } : null,
        isEdited: false,
        prompt: state.originalPrompt,
      }
    }
    default:
      return state
  }
}

interface UndoButtonProps {
  isVisible: boolean
  onUndoClick: () => void
}

interface ActionButtonProps {
  actionButton: React.ReactNode
}

const MemoizedObjectContextManager = memo(ObjectContextManager)

const UndoButton = memo(({ isVisible, onUndoClick }: UndoButtonProps) => (
  <Box sx={navigationButtonsSx}>
    {isVisible && (
      <IconButton
        size="small"
        onClick={onUndoClick}
      >
        <IconArrowBackUp size={16} />
      </IconButton>
    )}
  </Box>
))

const ActionButtonContainer = memo(({ actionButton }: ActionButtonProps) => (
  <Row sx={actionButtonContainerSx}>{actionButton}</Row>
))

interface PromptInputProps {
  suggestedPrompts?: ActionPromptOutput[]
  onSubmit?: (state: PromptState, dispatch: any) => void
  placeholder?: string
  // Context manager props
  workspaceId?: string
  contextStatus?: 'loading' | 'loaded' | 'error_capacity' | 'error_unknown'
  showStatus?: boolean
  showDebug?: boolean
  debugComponent?: 'page' | 'thread'
  contextObjects?: Partial<CRMObject>[]
  dismissedObjects?: Partial<CRMObject>[]
  handleSetContextObjects?: ({
    contextObjects,
    dismissedObjects,
  }: {
    contextObjects: Partial<CRMObject>[]
    dismissedObjects: Partial<CRMObject>[]
  }) => void
  // Optional action button to render in top right
  actionButton?: React.ReactNode
}

const PromptInput = ({
  suggestedPrompts = [],
  onSubmit,
  placeholder = "Let's take action. What should we make?",
  workspaceId,
  contextStatus,
  showStatus,
  showDebug,
  debugComponent,
  actionButton,
  contextObjects,
  dismissedObjects,
  handleSetContextObjects,
}: PromptInputProps) => {
  const initialState: PromptState = {
    template: (suggestedPrompts?.[0]?.template as Page) || null,
    isEdited: false,
    prompt: suggestedPrompts?.[0]?.shortPrompt || '',
    originalTemplate: (suggestedPrompts?.[0]?.template as Page) || null,
    originalPrompt: suggestedPrompts?.[0]?.shortPrompt || '',
  }

  const [state, dispatch] = useReducer(promptReducer, initialState)

  const textFieldInputSx = useMemo(() => getTextFieldInputSx(), [])

  const handleUndoClick = () => {
    dispatch({
      type: 'UNDO',
    })
  }

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== state.prompt) {
      dispatch({
        type: 'EDIT_PROMPT',
        payload: e.target.value,
      })
    }
  }

  const handleSubmit = () => {
    if (state.prompt.trim() && onSubmit) {
      onSubmit(state, dispatch)
    }
  }

  return (
    <Box sx={rootBoxSx}>
      <Box sx={containerWrapperSx}>
        <Box sx={upperContainerSx}>
          {suggestedPrompts?.length > 0 && (
            <Box sx={promptContainerSx}>
              <UndoButton
                isVisible={state.isEdited}
                onUndoClick={handleUndoClick}
              />
            </Box>
          )}

          <TextField
            multiline={true}
            minRows={2}
            maxRows={5}
            fullWidth={true}
            value={state.prompt}
            variant="standard"
            placeholder={suggestedPrompts?.length > 0 ? '' : placeholder}
            onChange={handleTextFieldChange}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            InputProps={{
              disableUnderline: true,
              sx: textFieldInputSx,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleSubmit}
                  >
                    <IconCircleArrowUpFilled size={20} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {actionButton && (
            <ActionButtonContainer actionButton={actionButton} />
          )}
        </Box>
        <Box sx={lowerContainerSx}>
          <MemoizedObjectContextManager
            contextObjects={contextObjects}
            dismissedObjects={dismissedObjects}
            handleSetContextObjects={handleSetContextObjects}
            workspaceId={workspaceId}
            contextStatus={contextStatus as ContextStatus}
            showStatus={showStatus}
            showDebug={showDebug}
            debugComponent={debugComponent}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default memo(PromptInput)
